import { useState } from "react";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { awards, teamMembers, stats } from "@/mocks/data";

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9rg6eue3oq4jqumu12g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      const parsed = (() => { try { return JSON.parse(responseText); } catch { return null; } })();
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
        setFormStatus("error");
        setFormError(serverMsg);
      } else {
        setFormStatus("error");
        setFormError(serverMsg || t("about.contact.formError"));
      }
    } catch {
      setFormStatus("error");
      setFormError(t("about.contact.formError"));
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于快猫视频",
    description: t("about.brand.desc"),
    url: "https://www.kuaimaovideo.com/about",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "快猫视频",
    url: "https://www.kuaimaovideo.com",
    description: t("site.description"),
    foundingDate: "2020",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "400-888-9999",
      contactType: "customer service",
      email: "contact@kuaimaovideo.com",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "望京科技园",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "快猫视频是免费的吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "快猫视频基础功能完全免费，包括视频拍摄、剪辑、发布和浏览。我们也有部分高级功能供有需要的创作者选择。",
        },
      },
      {
        "@type": "Question",
        name: "如何在快猫视频上发布视频？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "下载快猫视频APP后，点击底部中间的加号按钮，即可拍摄或从相册选择视频。完成剪辑后添加描述和标签，点击发布即可。",
        },
      },
      {
        "@type": "Question",
        name: "创作者如何获得收益？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "快猫视频提供多种变现方式：平台广告分成、直播打赏、品牌合作、电商带货和知识付费。创作者可根据自身情况选择适合的变现方式。",
        },
      },
      {
        "@type": "Question",
        name: "视频发布后可以修改吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "视频发布后，你可以在个人主页修改视频描述、标签和封面。但视频内容本身发布后不支持替换，如需修改请删除后重新发布。",
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={t("about.brand.desc")}
        keywords="快猫视频,关于快猫视频,短视频平台,视频创作工具,快猫团队"
        schema={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />
      <Breadcrumb items={[{ label: t("about.title") }]} />

      <section className="py-8 md:py-12 bg-background-50">
        <div className="container-page max-w-5xl">
          {/* Brand Story */}
          <div className="mb-16">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
              {t("about.brand.title")}
            </h1>
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="flex-1">
                <p className="text-foreground-700 leading-relaxed mb-4">
                  {t("about.brand.desc")}
                </p>
                <p className="text-foreground-700 leading-relaxed mb-6">
                  自成立以来，快猫视频始终坚持"技术驱动创作，内容连接世界"的理念，通过先进的AI推荐算法和强大的视频处理技术，为数亿用户提供了流畅、智能的短视频体验。我们的团队汇聚了来自全球顶尖互联网公司的技术专家和产品人才，致力于打造全球领先的短视频平台。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl bg-primary-50 border border-primary-100">
                    <h3 className="text-lg font-heading text-primary-600 mb-2">{t("about.brand.mission")}</h3>
                    <p className="text-sm text-foreground-600">我们致力于降低创作门槛，让每个人都能轻松成为内容创作者。</p>
                  </div>
                  <div className="p-5 rounded-xl bg-accent-50 border border-accent-100">
                    <h3 className="text-lg font-heading text-accent-600 mb-2">{t("about.brand.vision")}</h3>
                    <p className="text-sm text-foreground-600">成为全球最受欢迎的短视频创作与分享平台，连接每一个精彩瞬间。</p>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-80 flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20tech%20office%20space%20with%20warm%20coral%20and%20orange%20accent%20colors%2C%20collaborative%20workspace%2C%20video%20production%20equipment%2C%20young%20creative%20team%20atmosphere%2C%20natural%20light%2C%20innovative%20startup%20office%20aesthetic&width=400&height=500&seq=about-brand-image&orientation=portrait"
                  alt="快猫视频办公环境"
                  className="w-full rounded-xl object-cover"
                />
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mb-16 py-10 px-6 rounded-2xl bg-background-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl md:text-3xl font-heading text-primary-500">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Awards */}
          <div className="mb-16">
            <h2 className="text-2xl font-heading text-foreground-900 mb-3">
              {t("about.reputation.title")}
            </h2>
            <p className="text-foreground-500 mb-8">{t("about.reputation.desc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {awards.map((award) => (
                <div
                  key={`${award.year}-${award.title}`}
                  className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-award-line text-xl text-accent-500"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm">{award.title}</h4>
                    <p className="text-xs text-foreground-500 mt-1">{award.organization}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-accent-50 text-accent-600 whitespace-nowrap">
                      {award.year}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team */}
          <div className="mb-16">
            <h2 className="text-2xl font-heading text-foreground-900 mb-3">
              {t("about.team.title")}
            </h2>
            <p className="text-foreground-500 mb-8">{t("about.team.desc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mb-4"
                    loading="lazy"
                  />
                  <h4 className="font-semibold text-foreground-900">{member.name}</h4>
                  <p className="text-sm text-primary-500 mt-1">{member.title}</p>
                  <p className="text-xs text-foreground-500 mt-2">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact + Form */}
          <div className="mb-16" id="contact">
            <h2 className="text-2xl font-heading text-foreground-900 mb-3">
              {t("about.contact.title")}
            </h2>
            <p className="text-foreground-500 mb-8">{t("about.contact.desc")}</p>
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">{t("about.contact.email")}</h4>
                      <p className="text-sm text-foreground-500">contact@kuaimaovideo.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                      <i className="ri-phone-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">{t("about.contact.phone")}</h4>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">{t("about.contact.address")}</h4>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京科技园A座16层</p>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-6 rounded-xl overflow-hidden">
                  <iframe
                    title="快猫视频公司位置"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.836401514426!2d116.4805343!3d39.9867883!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0547b075d22fb%3A0x4c98e1f5e3c5b3e0!2sWangjing%20Science%20and%20Technology%20Park!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>

              <div className="flex-1">
                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-5">
                    {t("about.contact.form")}
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form="" id="about-contact-form">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t("about.contact.formName")}
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t("about.contact.formEmail")}
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t("about.contact.formMessage")}
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input name="phone_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      <button
                        type="submit"
                        className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t("about.contact.formSubmit")}
                      </button>
                    </div>
                    {formStatus === "success" && (
                      <p className="mt-4 text-sm text-green-600 text-center">
                        <i className="ri-check-line mr-1"></i>
                        {t("about.contact.formSuccess")}
                      </p>
                    )}
                    {formStatus === "error" && (
                      <p className="mt-4 text-sm text-red-500 text-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formError || t("about.contact.formError")}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div className="relative py-16 px-8 rounded-2xl overflow-hidden text-center" id="download">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20gradient%20background%20with%20coral%20and%20orange%20flowing%20abstract%20shapes%2C%20mobile%20app%20download%20concept%2C%20modern%20digital%20creative%20atmosphere%2C%20soft%20warm%20lighting&width=1200&height=400&seq=about-download-bg&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary-500/90"></div>
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-heading text-white mb-3">
                {t("home.download.title")}
              </h2>
              <p className="text-white/80 mb-8 max-w-md mx-auto">
                {t("home.download.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-600 text-base font-semibold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-apple-line text-xl"></i>
                  {t("home.download.ios")}
                </a>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/30 text-base font-medium hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-android-line text-xl"></i>
                  {t("home.download.android")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}